/**
 * Export, from the list you are looking at.
 *
 * ## The whole point: what is on screen is what is in the file
 *
 * Until this existed, exporting meant leaving the list, opening *Import and export*, and taking
 * the whole book. **Somebody who has filtered a list to thirty rows and presses Export expects
 * thirty rows** — and the commonest reason to press it is precisely to send *this part* of the
 * book to somebody: one landlord's buildings, this month's receipts, the jobs still open. Four
 * thousand rows in answer to that is worse than no file at all, because the person then trims it
 * by hand and sends whatever survives.
 *
 * **The filter is read from the address, not passed in by the screen**, and that is the design
 * rather than a shortcut. Every list in this application already keeps its filter in the query
 * string — `lib/list-params.ts` says why: so a filtered page survives a refresh, a back button and
 * being pasted to a colleague. That makes the address the single statement of what the person is
 * looking at, and an export built from it cannot drift from the screen. A button handed its
 * filters by each screen would be a second copy of the same state, and the day somebody adds a
 * filter and forgets the button, the file quietly stops matching the list — the exact failure
 * nobody notices, because a file of too many rows still looks like a file.
 *
 * **Which parameters travel is the server's list, not this file's.** `/data/exports` says what
 * each kind narrows by, and everything else in the address — `page`, a tab, a row that happens to
 * be open — is dropped. So `page=3` never becomes a file of one page, and a parameter this screen
 * invents cannot reach a query.
 *
 * ## Two formats, and they are not alternatives
 *
 * - **Spreadsheet** for reading and sending. Typed columns: a phone number keeps its leading zero
 *   and an amount is a number that adds up rather than text that looks like one.
 * - **CSV** for coming back in. It is the format the import reads, so it is offered by name and
 *   said so — somebody exporting in order to correct and re-import must not have to guess.
 *
 * ## Shut, never absent
 *
 * Export is a paid feature (ADR-0091). A plan that does not include it gets the button anyway with
 * a padlock on it, and pressing it says what would open it — a feature nobody can see is a feature
 * nobody buys. What a *role* may not do stays absent, as everywhere else: the server does not list
 * a kind this member cannot view, and with nothing listed nothing is drawn.
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Gate } from "@/lib/gating";
import { PadlockButton } from "./padlock";
import { Button } from "./ui";

interface ExportKind {
  kind: string;
  label: string;
  /** The query parameters this list narrows by, named as this screen's own address names them. */
  filters?: string[];
}

interface Catalogue {
  exports: ExportKind[];
  gate: Gate | null;
}

/**
 * The catalogue, asked for once and shared.
 *
 * The same query key the export panel uses, so a screen that has already loaded it draws its
 * button without a second request — and so both agree about what may be exported.
 */
function useExports() {
  return useQuery({
    queryKey: ["data-exports"],
    queryFn: () => api.get<Catalogue>("/data/exports"),
    staleTime: 5 * 60 * 1000,
  });
}

export function ExportButton({ kind, size = "md" }: { kind: string; size?: "sm" | "md" }) {
  const { data } = useExports();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  /* A press anywhere else closes it, and so does Escape. Without both, the menu stays open behind
     whatever the person went on to do. */
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const entry = data?.exports.find((row) => row.kind === kind);
  /* Nothing listed means this member may not have this list at all — see the note above. */
  if (!entry) return null;

  const gate = data?.gate ?? null;
  if (gate) return <PadlockButton gate={gate} size={size}>Export</PadlockButton>;

  /*
    The screen's own filter, narrowed to what the server said this kind understands.

    Empty values are dropped as well as absent ones: a select set back to "All" leaves `?status=`
    in the address on some screens, and a list asked to filter on the empty string answers with
    nothing.
  */
  const carried = new URLSearchParams();
  for (const name of entry.filters ?? []) {
    const value = params.get(name);
    if (value) carried.set(name, value);
  }
  const query = carried.toString();
  const href = (format: "csv" | "xlsx") =>
    `/api/v1/data/export/${kind}?${format === "xlsx" ? "format=xlsx&" : ""}${query}`;

  const narrowed = [...carried.keys()].length > 0;

  return (
    <div className="relative inline-block" ref={box} data-export-for={kind}>
      <Button
        variant="secondary"
        size={size}
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-export-open={kind}
        /* Said on the button rather than only inside the menu: the difference between "the list"
           and "this list" is the whole feature, and somebody who has filtered to thirty rows needs
           to know before they press which of the two they are about to get. */
        title={narrowed ? "Export what is on screen, with this filter" : "Export this list"}
      >
        Export{narrowed ? " (filtered)" : ""}
      </Button>

      {open ? (
        <div
          role="menu"
          data-export-menu={kind}
          className="absolute right-0 z-20 mt-1 w-64 rounded-xl border border-ink-200 bg-white p-1 shadow-lg"
        >
          <a
            role="menuitem"
            href={href("xlsx")}
            onClick={() => setOpen(false)}
            data-export-format="xlsx"
            className="block rounded-lg px-3 py-2 text-sm text-ink-800 hover:bg-ink-50"
          >
            <span className="font-medium">Spreadsheet</span>
            <span className="block text-xs text-ink-500">Opens ready to read. Best for sending on.</span>
          </a>
          <a
            role="menuitem"
            href={href("csv")}
            onClick={() => setOpen(false)}
            data-export-format="csv"
            className="block rounded-lg px-3 py-2 text-sm text-ink-800 hover:bg-ink-50"
          >
            <span className="font-medium">CSV</span>
            <span className="block text-xs text-ink-500">The format this list can be imported back from.</span>
          </a>
          {narrowed ? (
            <p className="px-3 py-2 text-xs text-ink-500">
              Only what this filter shows. Clear the filter for the whole list.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
